// ActivityReactionControl.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';

// Hooks
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { canUser, PermissionAction } from 'utilities/Permissions';
import {
  ACTIVITY_REACTION_DEFAULT,
  ACTIVITY_REACTION_TYPES,
  aggregateActivityReactions,
  canReactToActivityComment,
  canReactToActivityEvent,
  resolveMyActivityReaction,
} from 'node/activity/utils/activityReactions';
import { toggleActivityReaction } from 'node/activity/utils/toggleActivityReaction';

// CSS
import './ActivityReactionControl.css';

// Icons
import Icon from 'svg/Icon';

const PALETTE_HOVER_MS = 380;
const PALETTE_LEAVE_MS = 280;
const LONG_PRESS_MS = 480;

/**
 * ActivityReactionControl
 *
 * Quiet thumb_up trigger plus glyph chips for an activity event.
 *
 * @param {object} props
 * @param {object} [props.event]
 * @param {object} [props.comment]
 * @param {object} props.node
 * @param {object[]} [props.reactions]
 * @returns {ReactNode}
 */
const ActivityReactionControl = React.memo(({
  event,
  comment,
  node,
  reactions = [],
}) => {
  const { theme } = useTheme();
  const { user } = useUser();
  const isTouch = useIsMobile();

  const [paletteOpen, setPaletteOpen] = useState(false);
  const rootRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const pressTimerRef = useRef(null);
  const pressOpenedRef = useRef(false);

  const targetAllowed = comment
    ? canReactToActivityComment(comment)
    : canReactToActivityEvent(event);

  const canReact = useMemo(() => {
    if (!targetAllowed) return false;
    if (!node || !user) return false;
    return canUser(PermissionAction.COMMENT, node, user);
  }, [node, targetAllowed, user]);

  const { chips, mine } = useMemo(
    () => aggregateActivityReactions(reactions, { userKey: user?.key }),
    [reactions, user?.key]
  );

  const { existing, legacyLike } = useMemo(
    () => resolveMyActivityReaction(reactions, user?.key),
    [reactions, user?.key]
  );

  const clearHoverTimer = () => {
    if (hoverTimerRef.current) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  const clearPressTimer = () => {
    if (pressTimerRef.current) {
      window.clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };

  const closePalette = useCallback(() => {
    clearHoverTimer();
    setPaletteOpen(false);
  }, []);

  /**
   * handleSelect
   *
   * Sets, switches, or clears the current user's reaction.
   *
   * @param {string} reaction
   * @returns {Promise<void>}
   */
  const handleSelect = useCallback(async (reaction) => {
    if (!canReact) return;
    closePalette();
    try {
      await toggleActivityReaction({
        existing,
        legacyLike,
        event,
        comment,
        node,
        reaction,
        user,
      });
    } catch (error) {
      console.warn('[ActivityReactionControl] toggle failed:', error);
    }
  }, [canReact, closePalette, comment, event, existing, legacyLike, node, user]);

  /**
   * handleTriggerClick
   *
   * Default click writes thumbs up.
   *
   * @param {React.MouseEvent} clickEvent
   * @returns {void}
   */
  const handleTriggerClick = (clickEvent) => {
    clickEvent.preventDefault();
    clickEvent.stopPropagation();
    if (pressOpenedRef.current) {
      pressOpenedRef.current = false;
      return;
    }
    handleSelect(ACTIVITY_REACTION_DEFAULT);
  };

  const handleTriggerEnter = () => {
    if (isTouch) return;
    clearHoverTimer();
    if (paletteOpen) return;
    hoverTimerRef.current = window.setTimeout(() => {
      setPaletteOpen(true);
    }, PALETTE_HOVER_MS);
  };

  const handleRootEnter = () => {
    if (isTouch) return;
    clearHoverTimer();
  };

  const handleRootLeave = () => {
    if (isTouch) return;
    clearHoverTimer();
    hoverTimerRef.current = window.setTimeout(() => {
      setPaletteOpen(false);
    }, PALETTE_LEAVE_MS);
  };

  const handleTriggerPointerDown = (pointerEvent) => {
    if (pointerEvent.pointerType === 'mouse') return;
    pressOpenedRef.current = false;
    clearPressTimer();
    pressTimerRef.current = window.setTimeout(() => {
      pressOpenedRef.current = true;
      setPaletteOpen(true);
    }, LONG_PRESS_MS);
  };

  const handleTriggerPointerUp = () => {
    clearPressTimer();
  };

  useEffect(() => () => {
    clearHoverTimer();
    clearPressTimer();
  }, []);

  useEffect(() => {
    if (!paletteOpen) return undefined;

    const handlePointerDown = (pointerEvent) => {
      if (!rootRef.current?.contains(pointerEvent.target)) {
        closePalette();
      }
    };
    const handleKeyDown = (keyEvent) => {
      if (keyEvent.key === 'Escape') closePalette();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closePalette, paletteOpen]);

  if (!targetAllowed) return null;
  if (!canReact && chips.length === 0) return null;

  return (
    <div
      ref={rootRef}
      className={[
        'activity-reaction-control-container',
        chips.length > 0 ? 'has-chips' : '',
        paletteOpen ? 'is-open' : '',
        isTouch ? 'is-touch' : '',
      ].filter(Boolean).join(' ')}
      onMouseEnter={handleRootEnter}
      onMouseLeave={handleRootLeave}
      onClick={(clickEvent) => clickEvent.stopPropagation()}
    >

      {/* CHIPS */}
      {chips.length > 0 ? (
        <div className="activity-reaction-control-chips">
          {chips.map((chip) => (
            <button
              key={chip.key}
              className={[
                'activity-reaction-control-chip',
                chip.mine ? 'is-mine' : '',
              ].filter(Boolean).join(' ')}
              type="button"
              title={chip.names.join(', ')}
              disabled={!canReact}
              onClick={(clickEvent) => {
                clickEvent.preventDefault();
                clickEvent.stopPropagation();
                if (pressOpenedRef.current) {
                  pressOpenedRef.current = false;
                  return;
                }
                handleSelect(chip.key);
              }}
              onMouseEnter={handleTriggerEnter}
              onPointerDown={handleTriggerPointerDown}
              onPointerUp={handleTriggerPointerUp}
              onPointerCancel={handleTriggerPointerUp}
            >
              <span className="activity-reaction-control-chip-glyph" aria-hidden="true">
                {chip.glyph}
              </span>
              <span className="activity-reaction-control-chip-count">{chip.count}</span>
            </button>
          ))}
        </div>
      ) : null}

      {/* TRIGGER */}
      {canReact ? (
        <button
          className="activity-reaction-control-trigger"
          type="button"
          title="React"
          aria-label="React"
          aria-haspopup="true"
          aria-expanded={paletteOpen}
          onClick={handleTriggerClick}
          onMouseEnter={handleTriggerEnter}
          onPointerDown={handleTriggerPointerDown}
          onPointerUp={handleTriggerPointerUp}
          onPointerCancel={handleTriggerPointerUp}
        >
          <Icon
            iconKey="thumb_up"
            color={mine === ACTIVITY_REACTION_DEFAULT ? theme.foreground : theme.foregroundMuted}
            width={15}
            height={15}
          />
        </button>
      ) : null}

      {/* PALETTE */}
      {paletteOpen && canReact ? (
        <div
          className="activity-reaction-control-palette"
          role="menu"
          onMouseEnter={clearHoverTimer}
        >
          {ACTIVITY_REACTION_TYPES.map((entry) => (
            <button
              key={entry.key}
              className={[
                'activity-reaction-control-palette-item',
                mine === entry.key ? 'is-active' : '',
              ].filter(Boolean).join(' ')}
              type="button"
              role="menuitem"
              title={entry.label}
              onClick={(clickEvent) => {
                clickEvent.preventDefault();
                clickEvent.stopPropagation();
                handleSelect(entry.key);
              }}
            >
              {entry.glyph}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
});

ActivityReactionControl.displayName = 'ActivityReactionControl';

export default ActivityReactionControl;
